import React from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Collapse,
    Typography,
    useTheme,
    useMediaQuery,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    AddCircleOutline,
    Description,
    Info,
    Speed,
    LightbulbOutlined,
    AddTask,
    Group,
    NoteAdd,
    Tune,
    CheckCircle,
    Star,
    Flag,
    RocketLaunch,
    Business,
    AccountTree,
    Edit,
    PlayArrow,
    TaskAlt,
    Monitor,
    Analytics,
    Input,
    Groups,
    TipsAndUpdates,
    AutoFixHigh,
} from '@mui/icons-material';

interface PromptSuggestion {
    id: string;
    title: string;
    description: string;
    prompt: string;
    icon: string;
    category: string;
}

interface Category {
    id: string;
    title: string;
    description: string;
    icon: string;
    promptIds: string[];
}

interface PromptSuggestProps {
    onPromptSelect: (prompt: string) => void;
    categories?: Category[];
    suggestions?: PromptSuggestion[];
}

const iconMap: { [key: string]: React.ElementType } = {
    add_circle: AddCircleOutline,
    description: Description,
    info: Info,
    speed: Speed,
    lightbulb: LightbulbOutlined,
    add_task: AddTask,
    group: Group,
    note_add: NoteAdd,
    tune: Tune,
    check_circle: CheckCircle,
    star: Star,
    flag: Flag,
    rocket_launch: RocketLaunch,
    business: Business,
    account_tree: AccountTree,
    edit: Edit,
    play_arrow: PlayArrow,
    task_alt: TaskAlt,
    monitoring: Monitor,
    analytics: Analytics,
    input: Input,
    groups: Groups,
    tips_and_updates: TipsAndUpdates,
    magic: AutoFixHigh,
};

const PromptSuggest: React.FC<PromptSuggestProps> = ({
    onPromptSelect,
    categories,
    suggestions,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expandedId, setExpandedId] = React.useState<string | null>(null);

    // Build a quick lookup of suggestions by id
    const promptById = React.useMemo(() => {
        const map = new Map<string, PromptSuggestion>();
        (suggestions || []).forEach((s) => map.set(s.id, s));
        return map;
    }, [suggestions]);

    const handleCardClick = (prompt: string) => {
        onPromptSelect(prompt);
    };

    const handleCategoryToggle = (categoryId: string) => {
        setExpandedId((prev) => (prev === categoryId ? null : categoryId));
    };

    if (!categories || categories.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                py: 2,
                px: { xs: 1, sm: 2 }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'visible',
                        gap: 2,
                        pb: 1,
                        pt: 1,
                        px: 1,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.disabled,
                        borderRadius: 4,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        }
                    },
                    ...(isMobile && {
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        }
                    })
                }}
            >
                {categories.map((category) => {
                    const isExpanded = expandedId === category.id;
                    const IconComp = iconMap[category.icon] || AutoFixHigh;

                    return (
                        <Card
                            key={category.id}
                            variant="outlined"
                            sx={{
                                minWidth: { xs: 200, sm: 240 },
                                maxWidth: { xs: 200, sm: 240 },
                                height: 110,
                                flexShrink: 0,
                                bgcolor: 'background.paper',
                                border: '1px solid',
                                borderColor: isExpanded ? 'primary.main' : 'divider',
                                borderRadius: 5,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    borderColor: 'primary.light',
                                    boxShadow: '0 4px 12px rgba(84,189,193,0.10)',
                                }
                            }}
                        >
                            <CardActionArea
                                onClick={() => handleCategoryToggle(category.id)}
                                sx={{ height: '100%', borderRadius: 5 }}
                            >
                                <CardContent
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        p: 2
                                    }}
                                >
                                    <Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                mb: 1
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: 3,
                                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                                    color: 'primary.dark',
                                                    '& svg': {
                                                        fontSize: 18
                                                    }
                                                }}
                                            >
                                                <IconComp />
                                            </Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    fontSize: { xs: '0.85rem', sm: '0.9rem' }
                                                }}
                                            >
                                                {category.title}
                                            </Typography>
                                        </Box>

                                        <Tooltip
                                            title={category.description}
                                            placement="bottom-start"
                                            enterDelay={500}
                                            leaveDelay={200}
                                            disableInteractive={false}
                                            sx={{
                                                '& .MuiTooltip-tooltip': {
                                                    maxWidth: 300,
                                                    fontSize: '0.875rem',
                                                }
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    lineHeight: 1.4,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    fontSize: { xs: '0.75rem', sm: '0.78rem' },
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {category.description}
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    );
                })}
            </Box>

            {/* Expanded prompts for the selected category */}
            {categories.map((category) => {
                const isExpanded = expandedId === category.id;
                if (!isExpanded) return null;
                const prompts = category.promptIds
                    .map((pid) => promptById.get(pid))
                    .filter((p): p is PromptSuggestion => Boolean(p));
                if (prompts.length === 0) return null;

                return (
                    <Collapse key={`expanded-${category.id}`} in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                mt: 1.5,
                                mx: 1,
                                p: 1.5,
                                borderRadius: 4,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                                border: '1px solid',
                                borderColor: (t) => alpha(t.palette.primary.main, 0.18),
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            {prompts.map((prompt) => {
                                const IconComponent = iconMap[prompt.icon] || AutoFixHigh;
                                return (
                                    <Box
                                        key={prompt.id}
                                        onClick={() => handleCardClick(prompt.prompt)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: 1.25,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'transparent',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                                                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                                                transform: 'translateX(2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1.5,
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                                color: 'primary.main',
                                                flexShrink: 0,
                                                '& svg': { fontSize: 15 },
                                            }}
                                        >
                                            <IconComponent />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '0.82rem',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {prompt.title}
                                            </Typography>
                                            <Tooltip
                                                title={prompt.description}
                                                placement="left-start"
                                                enterDelay={500}
                                                leaveDelay={200}
                                                disableInteractive={false}
                                                sx={{
                                                    '& .MuiTooltip-tooltip': {
                                                        maxWidth: 300,
                                                        fontSize: '0.8rem',
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.72rem',
                                                        lineHeight: 1.3,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {prompt.description}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                );
            })}
        </Box>
    );
};

export default PromptSuggest;